import React, { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './MultiSelectDropdown.module.css'

export interface MultiSelectOption<V extends string> {
  value: V
  label: string
}

interface Props<V extends string> {
  value: V[]
  onChange: (next: V[]) => void
  options: ReadonlyArray<MultiSelectOption<V>>
  placeholder?: string
  disabled?: boolean
  className?: string
  hasError?: boolean
}

export function MultiSelectDropdown<V extends string>({
  value,
  onChange,
  options,
  placeholder = 'Alle',
  disabled = false,
  className,
  hasError,
}: Props<V>) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [close])

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) setOpen((p) => !p)
  }

  const toggleOption = (v: V, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v))
    } else {
      onChange([...value, v])
    }
  }

  const displayLabel = (() => {
    if (value.length === 0) return ''
    if (value.length <= 2) {
      return value
        .map((v) => options.find((o) => o.value === v)?.label ?? v)
        .join(', ')
    }
    return `${value.length} ausgewählt`
  })()

  return (
    <div ref={containerRef} className={clsx(styles.container, className)}>
      <div
        className={clsx(
          styles.trigger,
          disabled && styles.triggerDisabled,
          hasError && styles.triggerError,
          open && styles.triggerOpen,
        )}
        onClick={toggle}
        role="combobox"
        aria-expanded={open}
        aria-disabled={disabled}
      >
        <span className={clsx(styles.value, !displayLabel && styles.placeholder)}>
          {displayLabel || placeholder}
        </span>
        <svg
          className={clsx(styles.chevron, open && styles.chevronOpen)}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M5 8L10 13L15 8"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {open && !disabled && (
        <div className={styles.dropdown} role="listbox" aria-multiselectable="true">
          {options.map((opt) => {
            const checked = value.includes(opt.value)
            return (
              <div
                key={opt.value}
                className={clsx(styles.option, checked && styles.optionChecked)}
                onClick={(e) => toggleOption(opt.value, e)}
                role="option"
                aria-selected={checked}
              >
                <span className={clsx(styles.checkbox, checked && styles.checkboxChecked)}>
                  {checked && (
                    <svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {opt.label}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
